/**
 * Reading a cover image out of a page's Open Graph tags.
 *
 * Why this exists: iCal has no image property, so a Luma event arrives from
 * its feed with a title, a time and nothing to look at. The picture the
 * organiser actually chose is on the event page, in the `og:image` meta tag
 * every social platform reads.
 *
 * **That tag is not always the artwork, and on Luma it never is.** This file
 * used to say Luma's `og:image` was "the square card the event is promoted
 * with, which is exactly the 1:1 the feed tile wants". Fetched and measured,
 * it is 800x420 and it is a *composed* card: the Luma wordmark, the event
 * title set as text, an RSVP button, and the real cover inset on the right.
 * Cropped to a square for the feed it yields half a headline and half a
 * picture. `eventImageFrom` below is the fix — see its own note.
 *
 * A regex rather than a parser: pulling in an HTML parser to read one meta
 * tag would be the largest dependency in the Worker, and the failure mode of
 * getting it wrong is a missing picture, not a wrong one.
 *
 * The variants below are all real. Meta tags appear with `property=` (the OG
 * spec) and with `name=` (what several frameworks emit); the attributes come
 * in either order; quotes are single or double. A matcher that assumed one
 * shape would work on whichever page it was written against and silently
 * return null for the rest.
 */

/** Matches a `<meta>` tag and pulls its attributes out as one blob. */
const META_TAG = /<meta\b([^>]*)>/gi;

function attribute(attrs: string, name: string): string | null {
  const match = new RegExp(`\\b${name}\\s*=\\s*("([^"]*)"|'([^']*)'|([^\\s>]+))`, "i").exec(attrs);
  if (!match) return null;
  return match[2] ?? match[3] ?? match[4] ?? null;
}

/** The handful of entities that actually show up in a URL in markup. */
function decodeEntities(value: string): string {
  return value
    .replace(/&amp;/g, "&")
    .replace(/&#x2F;/gi, "/")
    .replace(/&#47;/g, "/")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

/**
 * The page's cover image, or null.
 *
 * `og:image` wins; `og:image:secure_url` and `twitter:image` are read as
 * fallbacks because some pages carry only those. Only absolute http(s) URLs
 * are returned — a relative path would need the page's base URL to resolve,
 * and a half-resolved image address renders as a broken picture, which is
 * worse than the gradient it would have replaced.
 */
export function ogImageFrom(html: string): string | null {
  const found = new Map<string, string>();

  for (const match of html.matchAll(META_TAG)) {
    const attrs = match[1] ?? "";
    const key = (attribute(attrs, "property") ?? attribute(attrs, "name"))?.toLowerCase();
    if (!key) continue;
    if (key !== "og:image" && key !== "og:image:secure_url" && key !== "twitter:image") {
      continue;
    }
    const content = attribute(attrs, "content");
    if (!content) continue;
    // First occurrence wins: a page listing several og:image tags is
    // declaring its preferred one first.
    if (!found.has(key)) found.set(key, decodeEntities(content.trim()));
  }

  for (const key of ["og:image", "og:image:secure_url", "twitter:image"]) {
    const value = found.get(key);
    if (value && /^https?:\/\//i.test(value)) return value;
  }
  return null;
}


/**
 * The name of the site a page belongs to, from `og:site_name`.
 *
 * Added for one specific, real failure. Two of the rooms on the founder's map
 * were episodes on a podcast's *own* site rather than on Apple, so there was
 * no show id in the url to look up, and the title gave nothing searchable —
 * "What Healthcare Can Learn From Waymo | Qualified Health CEO Dr. Justin
 * Norden —" names a guest and his company, and the show's name had been cut
 * off the end by the 80-character title cap. The page's `og:site_name` says
 * "The Heart of Healthcare Podcast" outright.
 *
 * `<title>` is deliberately NOT a fallback: on the same page it is the episode
 * title with the show appended, which is the string that was already useless.
 * Absent the tag, the answer is null and the resolve simply fails — the point
 * of this is a better search term, never a guessed one.
 */
export function siteNameFrom(html: string): string | null {
  for (const match of html.matchAll(META_TAG)) {
    const attrs = match[1] ?? "";
    const key = (attribute(attrs, "property") ?? attribute(attrs, "name"))?.toLowerCase();
    if (key !== "og:site_name") continue;
    const content = attribute(attrs, "content");
    const value = content ? decodeEntities(content.trim()) : "";
    if (value) return value;
  }
  return null;
}

/**
 * Do two urls sit on the same site?
 *
 * The verification step that makes a non-exact podcast match safe. A show's
 * feed publishes its own `<link>`, so when that link and the page the founder
 * was shown share a host, the show has said "that is me" — which is a fact
 * rather than a resemblance. `www.` is ignored because a feed and a page
 * disagree about it constantly and it has never meant anything.
 */
export function sameHost(a: string | null | undefined, b: string | null | undefined): boolean {
  const host = (url: string | null | undefined) => {
    if (!url) return null;
    try {
      return new URL(url).hostname.toLowerCase().replace(/^www\./, "");
    } catch {
      return null;
    }
  };
  const left = host(a);
  return Boolean(left) && left === host(b);
}

/**
 * Luma's real cover art, at 1:1 — because its `og:image` is a poster, not art.
 *
 * Measured on a live event page rather than assumed. The `og:image` is
 * `…/event-social/<shard>/<id>.png` at 800x420: a social card Luma composes
 * per event, carrying its wordmark, the title as rendered text, an RSVP
 * button, and the organiser's actual cover inset on the right. It is the right
 * image for a Twitter unfurl and the wrong one for a 72px square — a centre
 * crop of it is a slice of a headline.
 *
 * The artwork itself is a different asset, `…/event-covers/<shard>/<id>`, and
 * the page references it directly. Luma's own event page renders that asset at
 * **280x280 and 1920x1920** — square, because the cover *is* square. So this
 * is not us imposing a crop; it is us asking for the shape the source already
 * publishes.
 *
 * It cannot be derived from the `og:image` URL: `event-social` and
 * `event-covers` carry different ids for the same event, so nothing but the
 * page markup connects them. That is why this takes html rather than a url —
 * and why re-hydration, not a rewrite, is what fixes rows already stored.
 *
 * The one case that *is* derivable is the `og.luma.com/…?img=<encoded>`
 * variant, where the cover is a query parameter of the social card's own URL.
 * `lumaCoverFromSocialUrl` reads that.
 */

/** `images.lumacdn.com` serves through Cloudflare Images, so the transform is
 *  part of the path and any size can be asked for. Verified against the live
 *  CDN: swapping 800x420 for 256x256 returns a real 256x256. */
const LUMA_CDN = "https://images.lumacdn.com/cdn-cgi/image";
const LUMA_SQUARE_PX = 256;
const LUMA_TRANSFORM = `format=auto,fit=cover,dpr=1,background=white,quality=75,width=${LUMA_SQUARE_PX},height=${LUMA_SQUARE_PX}`;

/** `event-covers/<2-char shard>/<uuid>` with an optional extension — the page
 *  writes it bare, the `img=` parameter writes it with `.png`. */
const LUMA_COVER = /event-covers\/([a-z0-9]{2})\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(\.[a-z]{3,4})?/i;

function lumaSquareUrl(match: RegExpMatchArray): string {
  const [, shard, id, ext] = match;
  return `${LUMA_CDN}/${LUMA_TRANSFORM}/event-covers/${shard}/${id}${ext ?? ""}`;
}

/**
 * The cover hidden inside an `og.luma.com` social card URL, or null.
 *
 * That variant composes the card at render time and passes the real cover as
 * a percent-encoded `img=` parameter, so unlike the `event-social` case this
 * one needs no page.
 */
export function lumaCoverFromSocialUrl(url: string): string | null {
  if (!/^https?:\/\/og\.luma\.com\//i.test(url)) return null;

  // Hand-parsed rather than `new URL(...).searchParams`: this package compiles
  // with `types: []` — no DOM, no Node, no Workers — so the platform's URL is
  // not in scope here. See AGENTS.md, "packages/core compiles with types: []".
  const q = url.indexOf("?");
  if (q === -1) return null;
  for (const pair of url.slice(q + 1).split("&")) {
    if (!pair.startsWith("img=")) continue;
    let img = pair.slice(4);
    try {
      img = decodeURIComponent(img);
    } catch {
      // A malformed escape means this is not a URL we can read; the caller
      // falls back to the og:image, which is a poster but is at least real.
      return null;
    }
    const match = LUMA_COVER.exec(img);
    return match ? lumaSquareUrl(match) : null;
  }
  return null;
}

/**
 * The best cover image for an event page: the artwork when the page publishes
 * one separately, the `og:image` otherwise.
 *
 * Only Luma needs the special case today, and hardcoding one host here is the
 * same kind of fact as LinkedIn's pinned `Linkedin-Version` — a protocol
 * quirk of one service, written down where it is used rather than guessed at
 * every call site. Every other source (Meetup, Eventbrite, an organiser's own
 * site) publishes a real photograph as its `og:image`, which is exactly what
 * we want and is returned untouched.
 */
export function eventImageFrom(html: string): string | null {
  const og = ogImageFrom(html);

  // Only look for the Luma asset on a page that actually served a Luma image.
  // A different site that happens to link a Luma event should keep its own
  // picture rather than borrow one.
  if (og && /luma(cdn)?\.com/i.test(og)) {
    const embedded = lumaCoverFromSocialUrl(og);
    if (embedded) return embedded;
    const onPage = LUMA_COVER.exec(html);
    if (onPage) return lumaSquareUrl(onPage);
  }

  return og;
}
